import {
  SimulateMatchRequest,
  SimulateExecutionRequest,
  FullSimulationRequest,
  WorkflowSimulationResult,
  ExecutionSimulationResult,
  ApproverPreviewResult,
  FullSimulationResult,
  WorkflowRecommendationRequest,
  WorkflowRecommendationResult
} from '../types';
import { HttpClient } from './http-client';

/**
 * Service for workflow simulation operations.
 * All operations are read-only and do not persist any changes.
 */
export class WorkflowSimulatorService {
  constructor(private httpClient: HttpClient) {}

  /**
   * Simulate which workflow would match the provided JSON data.
   * This is a read-only operation that does not persist any changes.
   */
  async simulateMatch(
    divisionId: string,
    request: SimulateMatchRequest
  ): Promise<WorkflowSimulationResult> {
    return this.httpClient.post<WorkflowSimulationResult>(
      `/api/divisions/${divisionId}/simulator/match`,
      request
    );
  }

  /**
   * Simulate the execution of a specific workflow.
   * Shows what steps would be triggered and which approvers would be assigned.
   */
  async simulateExecution(
    divisionId: string,
    workflowId: string,
    request?: SimulateExecutionRequest
  ): Promise<ExecutionSimulationResult> {
    return this.httpClient.post<ExecutionSimulationResult>(
      `/api/divisions/${divisionId}/simulator/workflows/${workflowId}/execute`,
      request || {}
    );
  }

  /**
   * Preview the approvers configured for each level of a workflow.
   * Useful for validating workflow configuration before activation.
   */
  async previewApprovers(
    divisionId: string,
    workflowId: string
  ): Promise<ApproverPreviewResult> {
    return this.httpClient.get<ApproverPreviewResult>(
      `/api/divisions/${divisionId}/simulator/workflows/${workflowId}/approvers`
    );
  }

  /**
   * Perform a full workflow simulation - find matching workflow and simulate its execution.
   * Combines match and execution simulation in a single call.
   */
  async simulateFull(
    divisionId: string,
    request: FullSimulationRequest
  ): Promise<FullSimulationResult> {
    return this.httpClient.post<FullSimulationResult>(
      `/api/divisions/${divisionId}/simulator/full`,
      request
    );
  }

  /**
   * Analyze order data and generate workflow configuration recommendations.
   * Provides suggestions for matching criteria, steps, and skip conditions.
   */
  async getRecommendations(
    divisionId: string,
    request: WorkflowRecommendationRequest
  ): Promise<WorkflowRecommendationResult> {
    return this.httpClient.post<WorkflowRecommendationResult>(
      `/api/divisions/${divisionId}/simulator/recommendations`,
      request
    );
  }
}
